import type { ObjectTypename } from '@precon-tools-suite-by-shared/precon-graphql-types';
import {
    toURLSafeBase64,
    isValidUUID,
} from '@precon-tools-suite-by-shared/js-utils';
import {
    AuthContext,
    type AuthContextOptions,
} from '@precon-tools-suite-by-shared/shared-auth-js-ability-checks';
import {
    isRole,
    type Role,
    type Permission,
} from '@precon-tools-suite-by-shared/js-precon-rbac';

const encoder = new TextEncoder();

export function uuidToGlobalId(
    typename: ObjectTypename,
    uuid: string
): string | null {
    if (!isValidUUID(uuid)) return null;
    const canonicalUuid = uuid.toLowerCase();
    return toURLSafeBase64(encoder.encode(`${typename}:${canonicalUuid}`));
}

export type TokenClaims = {
    iss?: string | null;
    aud?: string | null;
    sub?: string | null;
    obj?: string | null;
    team?: string | null;
    prm?: string[] | string | null;
    roles?: string[] | string | null;
    scopes?: string[] | string | null;
    email?: string | null;
    emails?: string[] | string | null;
    idp?: string | null;
    tfp?: string | null;
    given_name?: string | null;
    family_name?: string | null;
    name?: string | null;
};

export function teamIdFromClaims(claims: TokenClaims): string | null {
    const team = claims.team;
    if (!team || team.trim() == '') return null;
    return uuidToGlobalId('Team', team);
}

export function userIdFromClaims(claims: TokenClaims): string | null {
    const userId = claims.sub ?? claims.obj;
    if (!userId || userId.trim() == '') return null;
    return uuidToGlobalId('User', userId);
}

export function rolesFromClaims(claims: TokenClaims): Role[] | null {
    let roles = claims.roles;
    if (!roles) return null;

    if (typeof roles === 'string') {
        roles = roles.split(' ');
    }

    if (!Array.isArray(roles)) return null;

    return roles.filter(isRole);
}

export function emailFromClaims(claims: TokenClaims): string | null {
    if (claims.email) return claims.email;
    if (typeof claims.emails === 'string') return claims.emails;
    if (Array.isArray(claims.emails)) return claims.emails[0] ?? null;
    return null;
}

export function extraPermissionsFromClaims(claims: TokenClaims): Permission[] {
    let perms = claims.prm;
    if (!perms) return [];

    if (typeof perms === 'string') {
        perms = perms.split(' ');
    }

    if (!Array.isArray(perms)) return [];

    return perms as Permission[];
}

export function scopesFromClaims(claims: TokenClaims): string[] {
    let scopes = claims.scopes;
    if (!scopes) return [];

    if (typeof scopes === 'string') {
        scopes = scopes.split(' ');
    }

    if (!Array.isArray(scopes)) return [];

    return scopes.map((scope) => scope.trim());
}

export function createAuthContextFromClaims(
    claims: TokenClaims,
    options: Omit<
        AuthContextOptions,
        'extraPermissions' | 'roles' | 'userId' | 'teamId'
    >
): AuthContext {
    return new AuthContext({
        extraPermissions: extraPermissionsFromClaims(claims),
        roles: rolesFromClaims(claims),
        teamId: teamIdFromClaims(claims),
        userId: userIdFromClaims(claims),
        ...options,
    });
}
